import { forwardRef } from 'react'
import {
  GetProps,
  GetRef,
  Spinner,
  Stack,
  Text,
  XStack,
  createStyledContext,
  styled,
  withStaticProperties,
} from 'tamagui'

type VariantType =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'disabled'
  | 'cta'

export const ButtonContext = createStyledContext<{
  link: boolean
  outline: boolean
  disabled: boolean
  icon: boolean | undefined
  loading: boolean
  variant: VariantType
}>({
  link: false,
  outline: false,
  disabled: false,
  icon: undefined,
  loading: false,
  variant: 'primary',
})
const colorByVariant = {
  primary: '$accent',
  secondary: '$cta-hover',
  danger: '$danger-300',
  success: '$success-300',
  disabled: '$text-disabled',
  cta: '$cta',
}
export const ButtonFrame = styled(Stack, {
  name: 'Button',
  tag: 'button',
  context: ButtonContext,
  focusable: true,
  role: 'button',
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'row',
  gap: '$size.spacing-xxxs',
  br: '$border-radius-sm',
  cursor: 'pointer',
  borderWidth: 2,
  borderColor: 'transparent',
  width: 'auto',
  minHeight: 40,
  variants: {
    size: {
      small: {
        paddingVertical: '$size.spacing-nano',
        paddingHorizontal: '$size.spacing-xxs',
      },
      medium: {
        paddingVertical: '$size.spacing-xxxs',
        paddingHorizontal: '$size.spacing-xs',
        height: 40,
      },
      large: {
        paddingVertical: '$size.spacing-xxs',
        paddingHorizontal: '$size.spacing-md',
        height: 48,
      },
    },
    variant: {
      primary: {
        bg: '$accent',
        color: '$text-on-accent',
        hoverStyle: {
          bg: '$accent-hover',
        },
        focusStyle: {
          bg: '$accent-pressed',
        },
        pressStyle: {
          bg: '$accent-pressed',
        },
        disabledStyle: {
          bg: '$bg-disabled',
          color: '$text-disabled',
        },
        disabledStyle: {
          bg: '$bg-disabled',
        },
      },
      secondary: {
        bg: 'transparent',
        borderColor: '$accent',
        color: '$accent',
        hoverStyle: {
          bg: '$accent-subtle',
          borderColor: '$accent-pressed',
          color: '$accent-pressed',
        },
        focusStyle: {
          bg: 'transparent',
          borderColor: '$accent-pressed',
          color: '$accent-pressed',
        },
        pressStyle: {
          bg: 'transparent',
          borderColor: '$accent-pressed',
          color: '$accent-pressed',
        },
        disabledStyle: {
          bg: 'transparent',
          borderColor: '$border-default',
          color: '$text-disabled',
        },
      },
      danger: {
        bg: '$danger-300',
        hoverStyle: {
          bg: '$danger-200',
        },
        focusStyle: {
          bg: '$danger-200',
        },
        pressStyle: {
          bg: '$danger-200',
          borderWidth: 2,
          borderStyle: 'solid',
          borderOffset: 1,
          borderColor: '$border-focus',
        },
      },
      success: {
        bg: '$success-300',
        hoverStyle: {
          bg: '$success-200',
        },
        focusStyle: {
          bg: '$success-200',
        },
        pressStyle: {
          bg: '$success-200',
          borderWidth: 2,
          borderStyle: 'solid',
          borderOffset: 1,
          borderColor: '$border-focus',
        },
      },
      disabled: {
        bg: '$bg-disabled',
        borderColor: '$bg-disabled',
      },
      // Call to action de conversão (compra): cor cherry da paleta, texto
      // sempre branco — usada nas páginas de venda.
      cta: {
        bg: '$cta',
        hoverStyle: {
          bg: '$cta-hover',
        },
        focusStyle: {
          bg: '$cta-hover',
        },
        pressStyle: {
          bg: '$cta-pressed',
        },
        disabledStyle: {
          bg: '$bg-disabled',
        },
      },
    },
    outline: {
      true: ({ variant }: { variant: VariantType }) => ({
        bg: 'transparent',
        borderColor: colorByVariant[variant],
        color: colorByVariant[variant],
        hoverStyle: {
          borderColor: colorByVariant[variant],
          bg: colorByVariant[variant],
          color: 'inherit',
        },
        focusStyle: {
          bg: colorByVariant[variant],
        },
        pressStyle: {
          borderWidth: 2,
          borderStyle: 'solid',
          borderOffset: 1,
          borderColor: colorByVariant[variant],
          bg: colorByVariant[variant],
        },
      }),
    },
    disabled: {
      true: {
        pointerEvents: 'none',
        cursor: 'not-allowed',
        opacity: 1,
        bg: 'transparent',
        borderColor: '$bg-disabled',
        color: '$icon-default',
        hoverStyle: {
          bg: 'transparent',
          borderColor: '$bg-disabled',
          color: '$icon-default',
        },
        focusStyle: {
          bg: 'transparent',
          borderColor: '$bg-disabled',
          color: '$icon-default',
        },
        pressStyle: {
          bg: 'transparent',
          borderColor: '$bg-disabled',
          color: '$icon-default',
        },
      },
    },
    link: {
      true: {
        borderColor: 'transparent',
        bg: 'transparent',
        hoverStyle: {
          borderColor: 'transparent',
          bg: 'transparent',
        },
        focusStyle: {
          bg: 'transparent',
        },
        pressStyle: {
          bg: 'transparent',
        },
      },
    },
    icon: {
      true: {
        borderColor: 'transparent',
        bg: 'transparent',
        hoverStyle: {
          bg: 'transparent',
        },
        focusStyle: {
          bg: 'transparent',
        },
        pressStyle: {
          bg: 'transparent',
        },
      },
    },
    loading: {
      true: {
        pointerEvents: 'none',
        bg: '$bg-disabled',
      },
    },
  } as const,
  defaultVariants: {
    variant: 'primary',
    size: 'medium',
  },
})

export type ButtonProps = GetProps<typeof ButtonFrame>

export const ButtonText = styled(Text, {
  name: 'ButtonText',
  context: ButtonContext,
  userSelect: 'none',
  ff: '$mulishBold',
  fontWeight: 700,
  fontSize: '$font-size-xs',
  textAlign: 'center',
  flexGrow: 1,
  variants: {
    variant: {
      primary: {
        color: '$text-on-accent',
        disabledStyle: {
          color: '$text-disabled',
        },
      },
      secondary: {
        color: '$accent',
        hoverStyle: {
          color: '$accent-pressed',
        },
        pressStyle: {
          color: '$accent-pressed',
        },
        disabledStyle: {
          color: '$text-disabled',
        },
      },
      danger: {
        color: '$text-on-accent',
      },
      success: {
        color: '$text-on-accent',
      },
      disabled: {
        color: '$text-disabled',
      },
      cta: {
        color: '$text-on-cta',
        disabledStyle: {
          color: '$text-disabled',
        },
      },
    },
    outline: {
      true: {
        color: 'inherit',
      },
    },
    disabled: {
      true: {
        color: '$icon-default',
      },
    },
    link: {
      true: {
        textDecorationLine: 'underline',
        hoverStyle: {
          opacity: 0.7,
        },
      },
    },
    loading: {
      true: {
        color: 'transparent',
      },
    },
  } as const,
  defaultVariants: {
    variant: 'primary',
  },
})

const ButtonIcon = ({ icon, loading, children }: ButtonProps) => {
  if (loading || !icon) return null
  return children
}

export const StyledSpinner = styled(Spinner, {
  name: 'StyledSpinner',
  context: ButtonContext,
  userSelect: 'none',
  color: '$icon-primary',
  zIndex: 2,
  variants: {
    size: {
      small: {
        size: 'small',
      },
      medium: {
        size: 'small',
      },
      large: {
        size: 'large',
      },
    },
  } as const,
  defaultVariants: {
    size: 'medium',
  },
})

const ButtonSpinner = ({ icon, loading }: ButtonProps) => {
  if (icon) return
  if (!loading) return

  return <StyledSpinner />
}

export const AppButton = withStaticProperties(ButtonFrame, {
  Props: ButtonContext.Provider,
  Text: ButtonText,
  Icon: ButtonIcon,
  Spinner: ButtonSpinner,
})

type Props = GetProps<typeof AppButton>

type Ref = GetRef<typeof AppButton>
// eslint-disable-next-line react/display-name
export const Button = forwardRef<Ref, Props>(
  ({ children, ...rest }: Props, ref) => {
    // Só o frame externo responde ao toque: os handlers de press vão nele, e o
    // conteúdo interno recebe apenas o resto. Espalhar tudo nos dois colava o
    // mesmo onPress em dois nós, e um clique no rótulo executava a ação duas
    // vezes.
    const { onPress, onPressIn, onPressOut, onLongPress, ...contentProps } =
      rest

    return (
      <AppButton
        ref={ref}
        {...contentProps}
        onPress={onPress}
        onPressIn={onPressIn}
        onPressOut={onPressOut}
        onLongPress={onLongPress}
      >
        {/* O conteúdo não participa do toque: o frame externo é o único
            responder. O tamagui transforma em responder qualquer componente
            com pseudo-estilo de press, e o `ButtonText` do variant secondary
            tem um — no nativo esse Text engolia o toque e o `onPress` do frame
            nunca era chamado ao tocar no rótulo. */}
        <Stack
          testID="button-content"
          pointerEvents="none"
          position="relative"
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          gap="$size.spacing-nano"
          width={rest.width || rest.w || 'auto'}
          height="100%"
        >
          {!rest.loading && (
            <AppButton.Icon {...contentProps}>{children}</AppButton.Icon>
          )}
          {rest.loading && (
            <XStack gap="$size.spacing-xxxs">
              <AppButton.Spinner {...rest} />
              <Text
                ff="$mulishBold"
                color="$text-disabled"
                fontSize="$font-size-xs"
                fontWeight="bold"
              >
                Carregando...
              </Text>
            </XStack>
          )}
          {!rest.icon && !rest.loading && (
            <AppButton.Text
              variant={rest.variant}
              outline={rest.outline}
              link={rest.link}
              loading={rest.loading}
              {...(contentProps as GetProps<typeof AppButton.Text>)}
              backgroundColor="transparent"
              testID="button-text"
            >
              {children}
            </AppButton.Text>
          )}
        </Stack>
      </AppButton>
    )
  },
)
